import React, { useState, useRef } from 'react';
import { onSubmitProps, numberWithSpaces, imageDimensions } from '../../../Constants/constants';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import EntityPage from '../../../Layout/EntityPage';
import { IOverrideEntityActions } from '../../../API/Interfaces/IEntityActions';
import ILocationBasicDto from '../../../API/Interfaces/ILocationBasicDto';
import LocationsServiceAPI from '../../../API/Services/LocationServiceAPI';
import GoalServiceAPI from '../../../API/Services/GoalServiceAPI';
import { Query, QueryResult, Column } from 'material-table';
import ILocationDetailsDto from '../../../API/Interfaces/ILocationDetailsDto';
import ISearchBaseDto from '../../../API/Interfaces/ISearchBaseDto';
import AppLink from '../../../Layout/Components/AppLink';
import { useParams } from 'react-router-dom';
import Modal from '../../../Layout/Components/Modal';
import ConnectModalForm from './ConnectModalForm';
import IGoalDetailsDto, { IGoalBasicDto } from '../../../API/Interfaces/IGoalDtos';
import LinkOffOutlinedIcon from '@material-ui/icons/LinkOffOutlined';
import { IconButton } from '@material-ui/core';
import ConfirmModal, { IConfirmModal } from '../../../Layout/Components/ConfirmModal';
import Helper from '../../../Utils/Helpers';
import GoalForm from '../../Goal/GoalForm';
import { DictImageType } from '../../../API/Interfaces/Enums';

interface IConfirmPopup {
	id: string;
	isVisible: boolean;
	text: string;
}

export const LocationGoals = () => {
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const tableRef = useRef();
	const [isConnectModalVisible, setIsConnectModalVisible] = useState(false);
	const { id } = useParams<{ id: string }>();
	const [entity, setEntity] = useState<IGoalDetailsDto>();
	const [open, setOpen] = useState(false);
	const [popupConfirmShown, setPopupConfirmShown] = useState<IConfirmPopup>();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const savedDialog = (message: string) => enqueueSnackbar(message, { variant: 'success' });

	const headers: Array<Column<IGoalBasicDto>> = [
		{
			title: t('repository.goals.goalName'),
			field: 'name',
			defaultSort: 'asc',
			render: (rowData) => {
				return (
					<AppLink
						to={'#'}
						onClick={async () => {
							const response = await fetchGoal(rowData.id);
							if (response) {
								setEntity({
									...response,
									targetAmount: numberWithSpaces(Number(response.targetAmount) / 100) || 0,
									currentAmount: numberWithSpaces(Number(response.currentAmount) / 100) || 0,
								});
								setOpen(true);
							}
						}}
					>
						{rowData.name}
					</AppLink>
				);
			},
		},
		{
			title: t('repository.goals.goalTitle'),
			field: 'title',
		},
		{
			title: t('common.table.unlinkHeader'),
			headerStyle: { textAlign: 'right' },
			sorting: false,
			render: (rowData) => (
				<IconButton
					onClick={() =>
						setPopupConfirmShown({ isVisible: true, id: rowData.id, text: t('location.removeConfirm', { goal: rowData.name }) })
					}
					style={{ marginLeft: 'auto', display: 'block' }}
				>
					<LinkOffOutlinedIcon style={{ color: 'black' }} />
				</IconButton>
			),
		},
	];

	const EntityActions: IOverrideEntityActions<ILocationDetailsDto, ILocationBasicDto> = {
		api: LocationsServiceAPI,
		presetDataCallback: (data: any) => {
			data.currentAmount = Number(parseFloat(data.currentAmount.replace(/ /g, '').replace(',', '.')).toFixed(2)) * 100;
			data.targetAmount = Number(parseFloat(data.targetAmount.replace(/ /g, '').replace(',', '.')).toFixed(2)) * 100 || 0;
			return data;
		},
		setModalOpen: (e) => setIsConnectModalVisible(e),
		getTableData: async function (query: Query<ILocationBasicDto>) {
			const result: QueryResult<ILocationBasicDto> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await EntityActions.api.GetLocationGoals(id, request);
				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;

					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, { variant: 'error' });
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const tableProps: any = {
		columns: headers,
		options: { paging: true },
	};

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: t('menu.goals'),
		tableProps: tableProps,
		isDetails: false, //props.isInDetails,
		//firstColumn: props.isInDetails ? 'name' : undefined,
	};

	function refreshTable() {
		tableRef.current && (tableRef.current! as any).onQueryChange();
	}

	const disconnectGoal = async () => {
		if (popupConfirmShown && popupConfirmShown.id && id) {
			Helper.setLoader(true);

			const response = await LocationsServiceAPI.DisconnectGoalFromLocation(id, popupConfirmShown!.id);
			if (response) {
				Helper.setLoader(false);
				if (response === 1) {
					savedDialog(t('location.removeGoalInfo'));
					setPopupConfirmShown({ id: '', text: '', isVisible: false });
					refreshTable();
				} else {
					errorDialog();
				}
			}
		}
	};

	const handleConfirmationModalProps: IConfirmModal = {
		handleOperation: disconnectGoal,
		isOpen: popupConfirmShown?.isVisible ?? false,
		handleClose: () => setPopupConfirmShown({ id: '', text: '', isVisible: false }),
		text: popupConfirmShown?.text ?? '',
		variant: 'outlined',
	};

	const callbacks: onSubmitProps = {
		presetDataCallback: EntityActions.presetDataCallback,
		setModalOpen: (e) => setOpen(e),
		api: GoalServiceAPI,
		refreshTable: refreshTable,
		setEntity: () => refreshTable(),
	};

	const fetchGoal = async function (id: string) {
		try {
			const response = await GoalServiceAPI.GetOne(id);
			if (response) return response;
			else {
				errorDialog();
			}
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	return (
		<>
			{open && (
				<Modal
					form={<GoalForm />}
					isOpen={open}
					callbacks={callbacks}
					entity={entity}
					modalTitle={t('repository.goals.editGoal')}
					withFileUpload
					withAdditionalFileUpload
					warningMessage={t('repository.goals.popupMessage')}
					fileLabel={t('repository.goals.mainPicture')}
					additionalFileLabel={t('repository.goals.additionalPicture')}
					imageType={DictImageType.Goal}
					imageDimensions={imageDimensions.mainGoalImage}
				/>
			)}
			{isConnectModalVisible && (
				<ConnectModalForm
					onClose={() => setIsConnectModalVisible(false)}
					isOpen={isConnectModalVisible}
					onRefresh={() => refreshTable()}
				/>
			)}

			{popupConfirmShown?.isVisible && <ConfirmModal {...handleConfirmationModalProps} />}
			<EntityPage {...entityPageProps} />
		</>
	);
};
